import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, SettingsCard, Toggle, useToast } from "@humansignal/ui";
import { useUpdatePageTitle, createTitleFromSegments } from "@humansignal/core";
import { ProjectContext } from "../../providers/ProjectProvider";
import { SettingsPageHeader } from "../../components/SettingsPageHeader/SettingsPageHeader";
import { cn } from "../../utils/bem";
import { useTranslation } from "../../i18n/LocaleProvider";
import "./AudioQCSettings.prefix.css";

/**
 * Audio QC settings — visible only when the project's
 * ``parsed_label_config`` contains an ``<AudioRecord>`` tag. Mirrors
 * the QualitySettings page pattern: each logical concern is its own
 * `<Section>` (a thin wrapper over `<SettingsCard>` with title +
 * description + per-section Save button in the footer slot), so
 * dirty tracking and save UX work the same way as the rest of the
 * Settings tabs (Quality, Review, Annotation).
 *
 * Sections (top to bottom):
 *   1. Pipeline — master switch + dispatch mode.
 *   2. Live capture — what the AudioRecord tag does in the browser.
 *   3. Pre-record silence gate — ambient-noise threshold check.
 *   4. Server-side checks — per-check enable + threshold.
 *   5. Failure policy + reviewer sampling.
 *
 * All five sections persist into the project model:
 *   - audio_qc_enabled, audio_qc_dispatch_mode, audio_qc_action_on_fail,
 *     qc_priority_in_review (top-level fields)
 *   - audio_qc_config (JSONField) — live + silence_gate + checks
 *   - review_settings.task_limit_percent (existing JSON field —
 *     wrapped here without forking the underlying sampling).
 */

const settingsDescription = cn("settings-description").toClassName();
const aqs = (suffix) => cn(`audio-qc-${suffix}`).toClassName();
const aqsBlock = (block, element) => cn(`audio-qc-${block}`).elem(element).toClassName();

// Shared per-section wrapper. Same shape as the one in QualitySettings —
// a SettingsCard with a footer that holds the section's summary line
// and a per-section Save button. Keeps dirty tracking local to the
// section so saving one card doesn't blow away unsaved drafts in
// neighbours.
const Section = ({ title, description, summary, onSave, saving, dirty, children }) => (
  <SettingsCard
    title={title}
    description={description}
    footer={
      <>
        <span className={settingsDescription} style={{ marginRight: "auto" }}>
          {summary}
        </span>
        <Button
          style={{ width: 120 }}
          type="button"
          variant="primary"
          size="small"
          onClick={onSave}
          disabled={saving || !dirty}
          aria-label={`Save ${title}`}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </>
    }
  >
    {children}
  </SettingsCard>
);

const ToggleField = ({ checked, onChange, label, description }) => (
  <div className={aqs("toggle")}>
    <Toggle label={label} description={description} checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
  </div>
);

const NumberField = ({ label, value, onChange, min, max, step, suffix }) => (
  <div className={aqs("field")}>
    <label className={aqsBlock("field", "label")}>{label}</label>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <input
        type="number"
        min={min}
        max={max}
        step={step ?? 1}
        className={aqs("input")}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      {suffix ? (
        <span style={{ color: "var(--color-neutral-content-subtler)", fontSize: "var(--font-size-body-small)" }}>
          {suffix}
        </span>
      ) : null}
    </div>
  </div>
);

// Update a deep path on a JSON config object without mutating the
// caller's reference. Used by the live + silence_gate + checks
// sections to drive their local draft state.
const setPath = (obj, path, value) => {
  const next = JSON.parse(JSON.stringify(obj || {}));
  let cur = next;
  for (let i = 0; i < path.length - 1; i += 1) {
    const k = path[i];
    if (cur[k] === undefined || cur[k] === null) cur[k] = {};
    cur = cur[k];
  }
  cur[path[path.length - 1]] = value;
  return next;
};

const cfgEqual = (a, b) => JSON.stringify(a ?? {}) === JSON.stringify(b ?? {});

// ── Deep merge two config objects. ``b`` overrides ``a``; nested
// objects merge key-by-key (so a template's ``checks.snr.min_db``
// override doesn't blow away a sibling ``checks.snr.good_db``).
// Arrays + scalars in ``b`` REPLACE — only plain objects merge.
const deepMerge = (a, b) => {
  if (b === null || b === undefined) return a;
  if (typeof a !== "object" || a === null || Array.isArray(a)) return b;
  if (typeof b !== "object" || Array.isArray(b)) return b;
  const out = { ...a };
  for (const k of Object.keys(b)) out[k] = deepMerge(a[k], b[k]);
  return out;
};

// ── 0. Recording environment template ─────────────────────────────────────────
//
// Top of the page: pick a preset that matches where your annotators
// will record. The preset is a partial ``audio_qc_config`` that deep-
// merges over the current project config — so individual thresholds
// the user has tuned in the sections below are preserved unless the
// template explicitly overrides them. After applying, the user can
// keep tweaking; "Save" on each section persists.

const TemplatesSection = ({ project, onSaved }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { updateProject } = useContext(ProjectContext);
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/audio-qc/templates/", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { templates: [] }))
      .then((data) => {
        if (!cancelled) setTemplates(data?.templates ?? []);
      })
      .catch(() => {
        /* network blip — empty dropdown */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const apply = useCallback(async () => {
    const tpl = templates.find((x) => x.key === selectedKey);
    if (!tpl) return;
    setSaving(true);
    const merged = deepMerge(project.audio_qc_config ?? {}, tpl.config ?? {});
    const res = await updateProject({ audio_qc_config: merged });
    setSaving(false);
    if (res?.$meta?.ok) {
      toast.show({ message: `Applied "${tpl.label}". Tweak any value below and save the section to persist.` });
      onSaved?.();
    } else {
      toast.show({ message: "Failed to apply template", type: "error" });
    }
  }, [templates, selectedKey, project.audio_qc_config, updateProject, toast, onSaved]);

  const selected = templates.find((x) => x.key === selectedKey);

  return (
    <Section
      title="Recording environment"
      description="Pick a preset that matches where your annotators will record. Applies sensible thresholds for that environment; you can fine-tune below."
      summary={selected ? selected.description : "No preset applied. Sections below show your current config."}
      onSave={apply}
      saving={saving}
      dirty={!!selected}
    >
      <div className={aqs("field")}>
        <label className={aqsBlock("field", "label")}>Preset</label>
        <select value={selectedKey} onChange={(e) => setSelectedKey(e.target.value)} className={aqs("select")}>
          <option value="">— Choose a recording environment —</option>
          {templates.map((tpl) => (
            <option key={tpl.key} value={tpl.key}>
              {tpl.label}
            </option>
          ))}
        </select>
      </div>
      {selected ? (
        <p
          style={{
            marginTop: 8,
            fontSize: "var(--font-size-body-small)",
            color: "var(--color-neutral-content-subtler)",
          }}
        >
          {selected.description}
        </p>
      ) : null}
    </Section>
  );
};

// ── 1. Pipeline ────────────────────────────────────────────────────────────────

const PipelineSection = ({ project, onSaved }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { updateProject } = useContext(ProjectContext);
  const [enabled, setEnabled] = useState(!!project.audio_qc_enabled);
  // Org policy: quality.require_audio_qc enforced -> the toggle locks
  // ON for recording projects (server rejects the off-PATCH anyway;
  // this is the visual).
  const [qcLocked, setQcLocked] = useState(false);
  useEffect(() => {
    if (!project?.id) return;
    fetch(`/api/projects/${project.id}/effective-policies/`, {
      credentials: "same-origin",
      cache: "no-store",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        const p = b?.policies?.["quality.require_audio_qc"];
        setQcLocked(!!(p?.enforced && p?.value));
      })
      .catch(() => {});
  }, [project?.id]);
  const [dispatch, setDispatch] = useState(project.audio_qc_dispatch_mode ?? "in_process");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEnabled(!!project.audio_qc_enabled);
    setDispatch(project.audio_qc_dispatch_mode ?? "in_process");
  }, [project.id, project.audio_qc_enabled, project.audio_qc_dispatch_mode]);

  const dirty = enabled !== !!project.audio_qc_enabled || dispatch !== (project.audio_qc_dispatch_mode ?? "in_process");

  const save = useCallback(async () => {
    setSaving(true);
    const res = await updateProject({
      audio_qc_enabled: enabled,
      audio_qc_dispatch_mode: dispatch,
    });
    setSaving(false);
    if (res?.$meta?.ok) {
      toast.show({ message: t("pages.settings.audio_qcsettings.pipeline_saved") });
      onSaved?.();
    } else toast.show({ message: t("pages.settings.audio_qcsettings.failed_to_save_pipeline"), type: "error" });
  }, [enabled, dispatch, updateProject, toast, onSaved]);

  const summary = !enabled
    ? "QC pipeline is off. Recordings save without automated checks."
    : dispatch === "mlbackend"
      ? "Cheap + heavy checks dispatched to an external MLBackend."
      : "Cheap checks run in-process on this Django on every annotation save.";

  return (
    <Section
      title={t("pages.settings.audio_qcsettings.pipeline")}
      description={t("pages.settings.audio_qcsettings.qc_runs_after_each_annotation_save_each_")}
      summary={summary}
      onSave={save}
      saving={saving}
      dirty={dirty}
    >
      <ToggleField
        checked={qcLocked ? true : enabled}
        onChange={qcLocked ? () => {} : setEnabled}
        label={t("pages.settings.audio_qcsettings.run_automated_quality_checks")}
        description={
          qcLocked
            ? "Always on for recording projects, set by your organization."
            : t("pages.settings.audio_qcsettings.autoset_to_on_when_this_project_has_an_a")
        }
      />
      <div className={aqs("field")} style={{ marginTop: 12 }}>
        <label className={aqsBlock("field", "label")}>
          {t("pages.settings.audio_qcsettings.where_to_run_the_checks")}
        </label>
        <select
          value={dispatch}
          onChange={(e) => setDispatch(e.target.value)}
          className={aqs("select")}
          disabled={!enabled}
        >
          <option value="in_process">{t("pages.settings.audio_qcsettings.inprocess_this_django_cheap_checks")}</option>
          <option value="mlbackend">
            {t("pages.settings.audio_qcsettings.external_mlbackend_heavy_checks_on_a_sep")}
          </option>
        </select>
      </div>
    </Section>
  );
};

// ── 2. Live capture ────────────────────────────────────────────────────────────

const LiveCaptureSection = ({ project, onSaved }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { updateProject } = useContext(ProjectContext);
  const initialLive = project.audio_qc_config?.live ?? {};
  const [live, setLive] = useState(initialLive);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLive(project.audio_qc_config?.live ?? {});
  }, [project.id, project.audio_qc_config]);

  const dirty = !cfgEqual(live, project.audio_qc_config?.live ?? {});

  const save = useCallback(async () => {
    setSaving(true);
    const merged = { ...(project.audio_qc_config || {}), live };
    const res = await updateProject({ audio_qc_config: merged });
    setSaving(false);
    if (res?.$meta?.ok) {
      toast.show({ message: t("pages.settings.audio_qcsettings.live_capture_settings_saved") });
      onSaved?.();
    } else toast.show({ message: t("pages.settings.audio_qcsettings.failed_to_save"), type: "error" });
  }, [live, project.audio_qc_config, updateProject, toast, onSaved]);

  const sr = live.sample_rate ?? 48000;
  const summary = `${sr / 1000} kHz, ${live.min_duration_s ?? 1}–${live.max_duration_s ?? 300}s, ${live.show_peak_meter ? "peak meter on" : "peak meter off"}.`;

  return (
    <Section
      title={t("pages.settings.audio_qcsettings.live_capture")}
      description={t("pages.settings.audio_qcsettings.what_the_recording_ui_shows_the_annotato")}
      summary={summary}
      onSave={save}
      saving={saving}
      dirty={dirty}
    >
      <ToggleField
        checked={!!live.show_peak_meter}
        onChange={(v) => setLive(setPath(live, ["show_peak_meter"], v))}
        label={t("pages.settings.audio_qcsettings.show_live_peak_meter")}
        description={t("pages.settings.audio_qcsettings.realtime_vu_bar_from_the_analysernode_ch")}
      />
      <ToggleField
        checked={!!live.show_snr_meter}
        onChange={(v) => setLive(setPath(live, ["show_snr_meter"], v))}
        label={t("pages.settings.audio_qcsettings.show_live_snr_noise_indicator")}
        description={t("pages.settings.audio_qcsettings.prerecording_1second_silence_calibration")}
      />
      <div className={`${aqs("row")} ${aqs("grid3")}`}>
        <NumberField
          label={t("pages.settings.audio_qcsettings.sample_rate_hz")}
          value={live.sample_rate ?? 48000}
          onChange={(v) => setLive(setPath(live, ["sample_rate"], v))}
        />
        <NumberField
          label={t("pages.settings.audio_qcsettings.max_duration")}
          suffix="s"
          value={live.max_duration_s ?? 300}
          onChange={(v) => setLive(setPath(live, ["max_duration_s"], v))}
        />
        <NumberField
          label={t("pages.settings.audio_qcsettings.min_duration")}
          suffix="s"
          step={0.1}
          value={live.min_duration_s ?? 1}
          onChange={(v) => setLive(setPath(live, ["min_duration_s"], v))}
        />
      </div>
    </Section>
  );
};

// ── 3. Pre-record silence gate ─────────────────────────────────────────────────

const SilenceGateSection = ({ project, onSaved }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { updateProject } = useContext(ProjectContext);
  const initial = project.audio_qc_config?.silence_gate ?? {};
  const [gate, setGate] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setGate(project.audio_qc_config?.silence_gate ?? {});
  }, [project.id, project.audio_qc_config]);

  const dirty = !cfgEqual(gate, project.audio_qc_config?.silence_gate ?? {});

  const save = useCallback(async () => {
    setSaving(true);
    const merged = { ...(project.audio_qc_config || {}), silence_gate: gate };
    const res = await updateProject({ audio_qc_config: merged });
    setSaving(false);
    if (res?.$meta?.ok) {
      toast.show({ message: t("pages.settings.audio_qcsettings.silence_gate_saved") });
      onSaved?.();
    } else toast.show({ message: t("pages.settings.audio_qcsettings.failed_to_save_silence_gate"), type: "error" });
  }, [gate, project.audio_qc_config, updateProject, toast, onSaved]);

  const summary = !gate.enabled
    ? "Silence gate off, recorder unlocks immediately."
    : `Lock recorder until ambient ≤ ${gate.threshold_db ?? -50} dBFS for ${gate.window_seconds ?? 2}s.`;

  return (
    <Section
      title={t("pages.settings.audio_qcsettings.prerecord_silence_gate")}
      description={t("pages.settings.audio_qcsettings.holds_the_recorder_closed_until_ambient_")}
      summary={summary}
      onSave={save}
      saving={saving}
      dirty={dirty}
    >
      <ToggleField
        checked={!!gate.enabled}
        onChange={(v) => setGate(setPath(gate, ["enabled"], v))}
        label={t("pages.settings.audio_qcsettings.require_quiet_environment_before_recordi")}
        description={t("pages.settings.audio_qcsettings.annotator_sees_a_live_noise_reading_reco")}
      />
      <div className={`${aqs("row")} ${aqs("grid3")}`}>
        <NumberField
          label={t("pages.settings.audio_qcsettings.threshold")}
          suffix="dBFS"
          step={1}
          value={gate.threshold_db ?? -50}
          onChange={(v) => setGate(setPath(gate, ["threshold_db"], v))}
        />
        <NumberField
          label={t("pages.settings.audio_qcsettings.window")}
          suffix="s"
          step={0.5}
          value={gate.window_seconds ?? 2}
          onChange={(v) => setGate(setPath(gate, ["window_seconds"], v))}
        />
        <NumberField
          label={t("pages.settings.audio_qcsettings.tolerance")}
          suffix="dB"
          step={0.5}
          value={gate.tolerance_db ?? 3}
          onChange={(v) => setGate(setPath(gate, ["tolerance_db"], v))}
        />
      </div>
      <p
        style={{
          margin: "12px 0 0",
          fontSize: "var(--font-size-body-small)",
          color: "var(--color-neutral-content-subtler)",
          lineHeight: 1.45,
        }}
      >
        Typical thresholds: −55 dBFS (recording booth), −50 dBFS (quiet room with NS on, default), −45 dBFS
        (residential), −35 dBFS (open office). Hysteresis (tolerance) prevents the gate from flickering when ambient
        sits exactly on the boundary.
      </p>
    </Section>
  );
};

// ── 4. Server-side checks ──────────────────────────────────────────────────────

const CHECK_ROWS = [
  ["duration", "Duration sanity", "Out-of-range recordings are usually abandoned takes."],
  ["dc_offset", "DC offset", "Detects a stuck mic / cheap USB interface bias."],
  ["peak", "True-peak", "ITU-R BS.1770 4× oversampled peak detector."],
  ["clipping", "Clipping", "% samples at threshold + consecutive-run rule."],
  ["loudness", "Loudness (LUFS)", "BS.1770 integrated loudness with target."],
  ["snr", "SNR", "VAD-gated speech vs noise power ratio (dB)."],
  ["speech_ratio", "Speech ratio", "Fraction of frames classified as speech."],
  ["leading_silence", "Leading silence", "Silence at the start of the recording."],
  ["trailing_silence", "Trailing silence", "Silence at the end."],
  ["internal_silence", "Internal silence", "Long mid-recording pause = likely skipped line."],
];

const ServerChecksSection = ({ project, onSaved }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { updateProject } = useContext(ProjectContext);
  const initial = project.audio_qc_config?.checks ?? {};
  const [checks, setChecks] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setChecks(project.audio_qc_config?.checks ?? {});
  }, [project.id, project.audio_qc_config]);

  const dirty = !cfgEqual(checks, project.audio_qc_config?.checks ?? {});

  const save = useCallback(async () => {
    setSaving(true);
    const merged = { ...(project.audio_qc_config || {}), checks };
    const res = await updateProject({ audio_qc_config: merged });
    setSaving(false);
    if (res?.$meta?.ok) {
      toast.show({ message: t("pages.settings.audio_qcsettings.server_checks_saved") });
      onSaved?.();
    } else toast.show({ message: t("pages.settings.audio_qcsettings.failed_to_save_server_checks"), type: "error" });
  }, [checks, project.audio_qc_config, updateProject, toast, onSaved]);

  const enabledCount = useMemo(() => CHECK_ROWS.filter(([k]) => (checks[k] || {}).enabled !== false).length, [checks]);

  return (
    <Section
      title={t("pages.settings.audio_qcsettings.serverside_checks")}
      description={t("pages.settings.audio_qcsettings.each_check_runs_after_submit_and_writes_")}
      summary={`${enabledCount} of ${CHECK_ROWS.length} checks enabled.`}
      onSave={save}
      saving={saving}
      dirty={dirty}
    >
      {CHECK_ROWS.map(([key, name, desc], i) => (
        <div
          key={key}
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            padding: "var(--spacing-tighter, 8px) 0",
            borderTop: i === 0 ? 0 : "1px solid var(--color-neutral-border-subtle)",
          }}
        >
          <div style={{ flex: "1 1 auto" }}>
            <ToggleField
              label={name}
              description={desc}
              checked={(checks[key] || {}).enabled !== false}
              onChange={(v) => setChecks(setPath(checks, [key, "enabled"], v))}
            />
          </div>
        </div>
      ))}
    </Section>
  );
};

// ── 5. Failure policy + reviewer sampling ──────────────────────────────────────

const POLICY_OPTIONS = [
  ["warn", "Warn but accept", "Annotator submits; QC failures appear as warnings only."],
  [
    "route_to_review",
    "Accept + force into review queue",
    "Default, flagged tasks always reach a reviewer regardless of sampling rate.",
  ],
  ["require_rerecord", "Reject, annotator must re-record", "Submit blocked; recorder reopens with the failure list."],
  ["block_submission", "Block, submit button disabled", "Hardest mode; use when QC is mission-critical."],
];

// ── Corpus purpose + crowd validation (community-speech plan A + G.1) ─────────

const PROFILE_OPTIONS = [
  ["custom", "Custom, my thresholds below", "Plain defaults plus your own fine-tuning."],
  [
    "clean_tts",
    "Clean speech (TTS / pronunciation)",
    "Studio-leaning: silence gate on, marginal noise forces a re-record.",
  ],
  [
    "robust_asr",
    "Real-world speech (robust ASR)",
    "Intelligibility-first: natural background noise is kept, not rejected, only corruption-level problems block.",
  ],
];

const VALIDATION_MODE_OPTIONS = [
  ["reviewers", "Reviewers only", "Managers/reviewers validate clips (classic review)."],
  ["crowd", "Crowd", "Contributors validate each other's clips; N agreeing votes resolve a clip."],
  ["hybrid", "Hybrid", "Crowd votes; reviewers keep the escalation queue and can override."],
];

const PROMPT_STYLE_OPTIONS = [
  ["read", "Read aloud (scripted)", "Speakers read the prompt verbatim, voters check the exact match."],
  [
    "spontaneous",
    "Spontaneous (topic/question)",
    "Speakers answer in their own words, voters judge language and audibility, no verbatim check.",
  ],
];

const CorpusPurposeSection = ({ project, onSaved }) => {
  const toast = useToast();
  const { updateProject } = useContext(ProjectContext);
  const cfg = project.audio_qc_config || {};
  const vcfg = cfg.validation || {};
  const pcfg = cfg.prompts || {};
  const [profile, setProfile] = useState(cfg.profile ?? "custom");
  const [mode, setMode] = useState(vcfg.mode ?? "reviewers");
  const [votes, setVotes] = useState(Number(vcfg.votes_to_validate ?? 2));
  const [promptStyle, setPromptStyle] = useState(cfg.prompt_style ?? "read");
  const [promptsOn, setPromptsOn] = useState(!!pcfg.enabled);
  const [maxWords, setMaxWords] = useState(Number(pcfg.max_words ?? 20));
  const [targetId, setTargetId] = useState("");
  const [perPromptCap, setPerPromptCap] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const c = project.audio_qc_config || {};
    setProfile(c.profile ?? "custom");
    setMode(c.validation?.mode ?? "reviewers");
    setVotes(Number(c.validation?.votes_to_validate ?? 2));
    setPromptStyle(c.prompt_style ?? "read");
    setPromptsOn(!!c.prompts?.enabled);
    setMaxWords(Number(c.prompts?.max_words ?? 20));
  }, [project.id, project.audio_qc_config]);

  const dirty =
    profile !== (cfg.profile ?? "custom") ||
    mode !== (vcfg.mode ?? "reviewers") ||
    Number(votes) !== Number(vcfg.votes_to_validate ?? 2) ||
    promptStyle !== (cfg.prompt_style ?? "read") ||
    promptsOn !== !!pcfg.enabled ||
    Number(maxWords) !== Number(pcfg.max_words ?? 20);

  const save = useCallback(async () => {
    setSaving(true);
    const n = Math.max(1, Math.min(9, Number(votes) || 2));
    const res = await updateProject({
      audio_qc_config: {
        ...(project.audio_qc_config || {}),
        profile,
        prompt_style: promptStyle,
        validation: {
          ...(project.audio_qc_config?.validation || {}),
          mode,
          votes_to_validate: n,
          votes_to_reject: n,
        },
        prompts: {
          ...(project.audio_qc_config?.prompts || {}),
          enabled: promptsOn,
          max_words: Math.max(3, Math.min(60, Number(maxWords) || 20)),
        },
      },
    });
    setSaving(false);
    if (res?.$meta?.ok) {
      toast.show({ message: "Corpus purpose saved." });
      onSaved?.();
    } else {
      toast.show({ message: "Failed to save corpus purpose.", type: "error" });
    }
  }, [profile, mode, votes, promptsOn, maxWords, project.audio_qc_config, updateProject, toast, onSaved]);

  const publish = useCallback(async () => {
    const id = Number(targetId);
    if (!id) return;
    setPublishing(true);
    try {
      const r = await fetch(`/api/projects/${project.id}/prompts/publish/`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": (document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/) || [])[1] || "",
        },
        body: JSON.stringify({
          target_project_id: id,
          ...(Number(perPromptCap) ? { max_recordings_per_prompt: Number(perPromptCap) } : {}),
        }),
      });
      const b = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.show({
          message: `Published ${b.published} accepted prompt${b.published === 1 ? "" : "s"} to project #${id}.`,
        });
      } else {
        toast.show({ message: b.detail || `Publish failed (${r.status})`, type: "error" });
      }
    } catch {
      toast.show({ message: "Publish failed, network error.", type: "error" });
    } finally {
      setPublishing(false);
    }
  }, [targetId, perPromptCap, project.id, toast]);

  const profileLabel = (PROFILE_OPTIONS.find(([k]) => k === profile) || [undefined, "Custom"])[1];
  const modeLabel = (VALIDATION_MODE_OPTIONS.find(([k]) => k === mode) || [undefined, "Reviewers"])[1];

  return (
    <Section
      title="Corpus purpose & validation"
      description="What is this corpus FOR? The profile tunes recording hints, automated checks, and the Listen page's validation criteria to the same bar, for real-world ASR corpora, natural background noise is kept, not rejected."
      summary={`${profileLabel}, validated by: ${modeLabel}${mode !== "reviewers" ? ` (${votes} votes to resolve)` : ""}`}
      onSave={save}
      saving={saving}
      dirty={dirty}
    >
      <div className={aqs("field")}>
        <label className={aqsBlock("field", "label")}>Audio profile</label>
        <select value={profile} onChange={(e) => setProfile(e.target.value)} className={aqs("select")}>
          {PROFILE_OPTIONS.map(([k, label]) => (
            <option key={k} value={k}>
              {label}
            </option>
          ))}
        </select>
        <p
          style={{
            marginTop: 6,
            fontSize: "var(--font-size-body-small)",
            color: "var(--color-neutral-content-subtler)",
          }}
        >
          {(PROFILE_OPTIONS.find(([k]) => k === profile) || [])[2]}
        </p>
      </div>
      <div className={aqs("field")}>
        <label className={aqsBlock("field", "label")}>Prompt style</label>
        <select value={promptStyle} onChange={(e) => setPromptStyle(e.target.value)} className={aqs("select")}>
          {PROMPT_STYLE_OPTIONS.map(([k, label]) => (
            <option key={k} value={k}>
              {label}
            </option>
          ))}
        </select>
        <p
          style={{
            marginTop: 6,
            fontSize: "var(--font-size-body-small)",
            color: "var(--color-neutral-content-subtler)",
          }}
        >
          {(PROMPT_STYLE_OPTIONS.find(([k]) => k === promptStyle) || [])[2]}
        </p>
      </div>
      <div className={aqs("field")}>
        <label className={aqsBlock("field", "label")}>Who validates clips</label>
        <select value={mode} onChange={(e) => setMode(e.target.value)} className={aqs("select")}>
          {VALIDATION_MODE_OPTIONS.map(([k, label]) => (
            <option key={k} value={k}>
              {label}
            </option>
          ))}
        </select>
        <p
          style={{
            marginTop: 6,
            fontSize: "var(--font-size-body-small)",
            color: "var(--color-neutral-content-subtler)",
          }}
        >
          {(VALIDATION_MODE_OPTIONS.find(([k]) => k === mode) || [])[2]}
        </p>
      </div>
      {mode !== "reviewers" && (
        <div className={aqs("field")}>
          <label className={aqsBlock("field", "label")}>Agreeing votes to resolve a clip</label>
          <input
            type="number"
            min={1}
            max={9}
            value={votes}
            onChange={(e) => setVotes(e.target.value)}
            className={aqs("select")}
            style={{ width: 90 }}
          />
        </div>
      )}
      <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--color-neutral-border-subtle)" }}>
        <ToggleField
          checked={promptsOn}
          onChange={setPromptsOn}
          label="Collect prompt sentences from contributors"
          description="Adds a 'Suggest a sentence' panel to the Listen page. Submissions require a license attestation and go through the same peer-vote loop as clips before they can be published."
        />
        {promptsOn && (
          <>
            <div className={aqs("field")} style={{ marginTop: 12 }}>
              <label className={aqsBlock("field", "label")}>Max words per sentence</label>
              <input
                type="number"
                min={3}
                max={60}
                value={maxWords}
                onChange={(e) => setMaxWords(e.target.value)}
                className={aqs("select")}
                style={{ width: 90 }}
              />
            </div>
            <div className={aqs("field")} style={{ marginTop: 12 }}>
              <label className={aqsBlock("field", "label")}>Publish accepted prompts to project (ID)</label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="number"
                  min={1}
                  value={targetId}
                  onChange={(e) => setTargetId(e.target.value)}
                  placeholder="e.g. 42"
                  className={aqs("select")}
                  style={{ width: 120 }}
                />
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={perPromptCap}
                  onChange={(e) => setPerPromptCap(e.target.value)}
                  placeholder="recordings/prompt"
                  title="Max recordings per prompt in the target project (blank = target's overlap setting)"
                  className={aqs("select")}
                  style={{ width: 150 }}
                />
                <Button
                  type="button"
                  variant="neutral"
                  size="small"
                  onClick={publish}
                  disabled={publishing || !Number(targetId)}
                >
                  {publishing ? "Publishing…" : "Publish accepted"}
                </Button>
              </div>
              <p
                style={{
                  marginTop: 6,
                  fontSize: "var(--font-size-body-small)",
                  color: "var(--color-neutral-content-subtler)",
                }}
              >
                Creates one recording task per crowd-accepted sentence in the target project (same organization),
                carrying its license and source. Idempotent — already-published prompts are skipped.
              </p>
            </div>
          </>
        )}
      </div>
    </Section>
  );
};

const FailurePolicySection = ({ project, onSaved }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { updateProject } = useContext(ProjectContext);
  const [action, setAction] = useState(project.audio_qc_action_on_fail ?? "route_to_review");
  const [priority, setPriority] = useState(project.qc_priority_in_review !== false);
  const [reviewPct, setReviewPct] = useState(Number(project.review_settings?.task_limit_percent ?? 100));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAction(project.audio_qc_action_on_fail ?? "route_to_review");
    setPriority(project.qc_priority_in_review !== false);
    setReviewPct(Number(project.review_settings?.task_limit_percent ?? 100));
  }, [project.id, project.audio_qc_action_on_fail, project.qc_priority_in_review, project.review_settings]);

  const dirty =
    action !== (project.audio_qc_action_on_fail ?? "route_to_review") ||
    priority !== (project.qc_priority_in_review !== false) ||
    Number(reviewPct) !== Number(project.review_settings?.task_limit_percent ?? 100);

  const save = useCallback(async () => {
    setSaving(true);
    const reviewSettings = {
      ...(project.review_settings || {}),
      task_limit_percent: Math.max(0, Math.min(100, Number(reviewPct) || 0)),
    };
    const res = await updateProject({
      audio_qc_action_on_fail: action,
      qc_priority_in_review: priority,
      review_settings: reviewSettings,
    });
    setSaving(false);
    if (res?.$meta?.ok) {
      toast.show({ message: t("pages.settings.audio_qcsettings.failure_policy_saved") });
      onSaved?.();
    } else toast.show({ message: t("pages.settings.audio_qcsettings.failed_to_save_policy"), type: "error" });
  }, [action, priority, reviewPct, project.review_settings, updateProject, toast, onSaved]);

  const actionLabel = (POLICY_OPTIONS.find(([k]) => k === action) || [
    undefined,
    "Accept + force into review queue",
  ])[1];
  const summary = `${actionLabel}. Reviewers sample ${reviewPct}% of tasks${priority ? "; flagged work surfaces first" : ""}.`;

  return (
    <Section
      title={t("pages.settings.audio_qcsettings.failure_policy_reviewer_sampling")}
      description={t("pages.settings.audio_qcsettings.what_happens_when_at_least_one_check_fai")}
      summary={summary}
      onSave={save}
      saving={saving}
      dirty={dirty}
    >
      <div className={aqs("field")}>
        <label className={aqsBlock("field", "label")}>
          {t("pages.settings.audio_qcsettings.action_when_qc_fails")}
        </label>
        <select value={action} onChange={(e) => setAction(e.target.value)} className={aqs("select")}>
          {POLICY_OPTIONS.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      </div>
      <div style={{ marginTop: 12 }}>
        <ToggleField
          checked={priority}
          onChange={setPriority}
          label={t("pages.settings.audio_qcsettings.surface_qcflagged_tasks_first_in_the_rev")}
          description={t("pages.settings.audio_qcsettings.when_on_reviewers_see_flagged_work_befor")}
        />
      </div>
      <div className={aqs("field")} style={{ marginTop: 12 }}>
        <label className={aqsBlock("field", "label")}>
          {t("pages.settings.audio_qcsettings.reviewer_sampling_rate")}
        </label>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="number"
            min={0}
            max={100}
            className={aqs("input")}
            value={reviewPct}
            onChange={(e) => setReviewPct(Number(e.target.value))}
          />
          <span style={{ color: "var(--color-neutral-content-subtler)", fontSize: "var(--font-size-body-small)" }}>
            %
          </span>
        </div>
        <p
          style={{
            margin: "6px 0 0",
            fontSize: "var(--font-size-body-small)",
            color: "var(--color-neutral-content-subtler)",
          }}
        >
          Existing setting (review_settings.task_limit_percent). 100 = review everything; 20 = stable random 20% subset
          per project.
        </p>
      </div>
    </Section>
  );
};

// ── Page shell ─────────────────────────────────────────────────────────────────

export const AudioQCSettings = () => {
  const { t } = useTranslation();
  const { project, fetchProject } = useContext(ProjectContext);
  useUpdatePageTitle(createTitleFromSegments([project?.title, "Audio QC"]));
  const onSaved = useCallback(() => {
    fetchProject?.();
  }, [fetchProject]);

  if (!project?.id) return null;

  return (
    <div className={cn("settings-page").toClassName()}>
      <SettingsPageHeader
        title={t("pages.settings.audio_qcsettings.audio_qc")}
        subtitle={
          <>
            Automated quality checks run on every recording your annotators submit. This tab is visible because the
            project's labeling config contains an <code>{t("pages.settings.audio_qcsettings.ltaudiorecordgt")}</code>{" "}
            tag.
          </>
        }
      />
      <div className={cn("settings-page").elem("body").toClassName()}>
        <TemplatesSection project={project} onSaved={onSaved} />
        <CorpusPurposeSection project={project} onSaved={onSaved} />
        <PipelineSection project={project} onSaved={onSaved} />
        <LiveCaptureSection project={project} onSaved={onSaved} />
        <SilenceGateSection project={project} onSaved={onSaved} />
        <ServerChecksSection project={project} onSaved={onSaved} />
        <FailurePolicySection project={project} onSaved={onSaved} />
      </div>
    </div>
  );
};

AudioQCSettings.menuItem = "Audio QC";
AudioQCSettings.title = "Audio QC";
AudioQCSettings.path = "/audio-qc";
AudioQCSettings.exact = true;

/** Detect whether the project's parsed_label_config contains an
 *  AudioRecord tag. The Settings router uses this to decide whether
 *  to surface the menu item. */
export const projectHasAudioRecord = (project) => {
  if (!project?.parsed_label_config) return false;
  const cfg =
    typeof project.parsed_label_config === "string"
      ? safeParse(project.parsed_label_config)
      : project.parsed_label_config;
  if (!cfg || typeof cfg !== "object") return false;
  // Audio QC covers every recording control: <AudioRecord> and
  // <VideoRecord> (container checks, duration, Listen validation).
  return Object.values(cfg).some((control) => {
    const t = (control && control.type) || "";
    return ["audiorecord", "videorecord"].includes(String(t).toLowerCase());
  });
};

const safeParse = (s) => {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
};
